import { createApi } from "@reduxjs/toolkit/query/react";
import { setSubscriptionLoading, setSubscriptionStatus } from "../slice/authSlice";
import { baseQueryWithReauth } from "./baseQueryWithReauth";
import { walletApi } from "./walletApi";
import { userApi } from "./userApi";
import { postApi } from "./postApi";

export interface OneTimePurchaseRequest {}

export interface OneTimePurchaseResponse {
  success: boolean;
  message: string;
  paymentUrl?: string;
}

export interface SubscriptionUser {
  uid: string;
  email: string;
  username: string;
  image: {
    url: string;
    mimeType: string;
  } | null;
  createdAt: string;
  isPremium: boolean;
}

export interface MySubscriptionResponse {
  uid: string;
  type: string;
  status: string;
  startDate: string;
  endDate: string;
  user: SubscriptionUser;
}
export interface PremiumSubscriptionSatsResponse {
  value: number;
}

export const premiumApi = createApi({
  reducerPath: "premiumApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Premium", "WalletBalance"],
  endpoints: (build) => ({
    oneTimePurchase: build.mutation<OneTimePurchaseResponse, OneTimePurchaseRequest>({
      query: (body) => ({
        url: "/subscriptions?type=MONTHLY",
        method: "POST",
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setSubscriptionStatus("ACTIVE"));
          dispatch(walletApi.util.invalidateTags(["WalletBalance"]));
          dispatch(userApi.util.invalidateTags(["User", "UserSidebar"]));
          dispatch(postApi.util.invalidateTags([{ type: "Post", id: "LIST" }]));
          // Delay Premium tag invalidation to let the server create the subscription record
          setTimeout(() => {
            dispatch(premiumApi.util.invalidateTags(["Premium"]));
          }, 3000);
        } catch {
          // purchase failed, status unchanged
        }
      },
    }),
    getMySubscription: build.query<MySubscriptionResponse[], void>({
      query: () => ({
        url: "/subscriptions/my",
        method: "GET",
      }),
      providesTags: ["Premium"],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        dispatch(setSubscriptionLoading(true));
        try {
          const { data } = await queryFulfilled;
          if (Array.isArray(data) && data.length > 0) {
            const hasActiveSubscription = data.some(
              (subscription) => new Date(subscription.endDate) > new Date()
            );
            dispatch(setSubscriptionStatus(hasActiveSubscription ? "ACTIVE" : "INACTIVE"));
          } else {
            dispatch(setSubscriptionStatus("INACTIVE"));
          }
        } catch {
          dispatch(setSubscriptionStatus("INACTIVE"));
        } finally {
          dispatch(setSubscriptionLoading(false));
        }
      },
    }),
    getPremiumSubscriptionPrice: build.query<PremiumSubscriptionSatsResponse, void>({
      query: () => ({
        url: "system-configuration/premium-subscription-sats",
        method: "GET",
      }),
      providesTags: ["Premium"],
    }),
    cancelSubscription: build.mutation<{ success: boolean; message: string }, string>({
      query: (uid) => ({
        url: `subscriptions/${uid}/cancel`,
        method: "PATCH",
      }),
      invalidatesTags: ["Premium"],
    }),
  }),
});

export const {
  useOneTimePurchaseMutation,
  useGetMySubscriptionQuery,
  useGetPremiumSubscriptionPriceQuery,
  useCancelSubscriptionMutation,
} = premiumApi;
